import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { curriculaAPI, trainingNodesAPI, quizzesAPI, sopsAPI, usersAPI, assignmentsAPI, activitiesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  GraduationCap, 
  FileQuestion,
  BookOpen,
  Edit2,
  Trash2,
  Save,
  Calendar,
  UserPlus,
  Briefcase,
  PlayCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const JOB_ROLES = [
  "Clin Ops",
  "Reg Ops", 
  "Quality Ops",
  "Recr Ops",
  "eSource Ops",
  "Data Ops",
  "Budget Ops"
];

const Curricula = () => {
  const { canManage } = useAuth();
  const [curricula, setCurricula] = useState([]);
  const [trainingNodes, setTrainingNodes] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [sops, setSOPs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    job_role: 'Clin Ops',
    training_node_ids: [],
    quiz_ids: [],
    sop_ids: [],
    activity_ids: [],
    duration_days: 30,
  });

  useEffect(() => {
    if (canManage) {
      loadData();
    }
  }, [canManage]);

  const loadData = async () => {
    try {
      const [curriculaRes, nodesRes, quizzesRes, sopsRes, usersRes, activitiesRes] = await Promise.all([
        curriculaAPI.getAll(),
        trainingNodesAPI.getAll(),
        quizzesAPI.getAll(),
        sopsAPI.getAll(),
        usersAPI.getAll(),
        activitiesAPI.getAll()
      ]);
      setCurricula(curriculaRes.data);
      setTrainingNodes(nodesRes.data);
      setQuizzes(quizzesRes.data);
      setSOPs(sopsRes.data);
      setUsers(usersRes.data.filter(u => u.role === 'employee'));
      setActivities(activitiesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get trainings that are mandatory for a specific job role
  const getMandatoryTrainings = (jobRole) => {
    return trainingNodes.filter(node => 
      node.role_requirements && node.role_requirements[jobRole] === 'M'
    );
  };

  const getMandatorySOPs = (jobRole) => {
    return sops.filter(sop => 
      sop.role_requirements && sop.role_requirements[jobRole] === 'M'
    );
  };

  const handleCreate = () => {
    setSelectedCurriculum(null);
    setForm({
      title: '',
      description: '',
      job_role: 'Clin Ops',
      training_node_ids: [],
      quiz_ids: [],
      sop_ids: [],
      activity_ids: [],
      duration_days: 30,
    });
    setShowEditor(true);
  };

  const handleEdit = (curriculum) => {
    setSelectedCurriculum(curriculum);
    setForm({
      title: curriculum.title,
      description: curriculum.description || '',
      job_role: curriculum.job_role || 'Clin Ops',
      training_node_ids: curriculum.training_node_ids || [],
      quiz_ids: curriculum.quiz_ids || [],
      sop_ids: curriculum.sop_ids || [],
      activity_ids: curriculum.activity_ids || [],
      duration_days: curriculum.duration_days || 30,
    });
    setShowEditor(true);
  };

  const handleAutoPopulate = () => {
    // Auto-populate with mandatory items for the selected job role
    const mandatoryTrainings = getMandatoryTrainings(form.job_role);
    const mandatorySOPs = getMandatorySOPs(form.job_role);
    const onboardingActivities = activities.filter(a => a.activity_type === 'onboarding');

    setForm({
      ...form,
      training_node_ids: mandatoryTrainings.map(t => t.id),
      sop_ids: mandatorySOPs.map(s => s.id),
      activity_ids: onboardingActivities.map(a => a.id),
    });
    toast.success(`Auto-populated with ${mandatoryTrainings.length} trainings, ${mandatorySOPs.length} SOPs, ${onboardingActivities.length} activities`);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Please enter a curriculum title');
      return;
    }

    try {
      if (selectedCurriculum) {
        await curriculaAPI.update(selectedCurriculum.id, form);
        toast.success('Curriculum updated');
      } else {
        await curriculaAPI.create(form);
        toast.success('Curriculum created');
      }
      setShowEditor(false);
      loadData();
    } catch (error) {
      console.error('Failed to save curriculum:', error);
      toast.error('Failed to save curriculum');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this curriculum?')) return;
    
    try {
      await curriculaAPI.delete(id);
      toast.success('Curriculum deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete curriculum');
    }
  };

  const handleOpenAssign = (curriculum) => {
    setSelectedCurriculum(curriculum);
    setSelectedUserId('');
    setShowAssignDialog(true);
  };

  const handleAssign = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    try {
      await assignmentsAPI.create({
        user_id: selectedUserId,
        curriculum_id: selectedCurriculum.id,
      });
      toast.success('Curriculum assigned successfully');
      setShowAssignDialog(false);
    } catch (error) {
      console.error('Failed to assign:', error);
      toast.error('Failed to assign curriculum');
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedCurriculum) return;
    
    try {
      const response = await assignmentsAPI.bulkAssignByRole(
        selectedCurriculum.id, 
        selectedCurriculum.job_role
      );
      toast.success(`Assigned to ${response.data.assigned_count} users (${response.data.skipped_count} already assigned)`);
      setShowAssignDialog(false);
    } catch (error) {
      console.error('Failed to bulk assign:', error);
      toast.error('Failed to bulk assign curriculum');
    }
  };

  const toggleItem = (field, id) => {
    const current = form[field];
    if (current.includes(id)) {
      setForm({ ...form, [field]: current.filter(i => i !== id) });
    } else {
      setForm({ ...form, [field]: [...current, id] });
    }
  };

  if (!canManage) {
    return (
      <div className="text-center py-12">
        <GraduationCap className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">You don't have permission to access this page</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="curricula-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-blue-600" />
            Curricula
          </h1>
          <p className="text-slate-600 mt-1">
            Create and manage onboarding curricula by job role
          </p>
        </div>
        <Button 
          onClick={handleCreate}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="create-curriculum-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Curriculum
        </Button>
      </div>

      {/* Curricula Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {curricula.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <GraduationCap className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No curricula created yet</p>
            <Button onClick={handleCreate} className="mt-4">
              Create your first curriculum
            </Button>
          </div>
        ) : (
          curricula.map((curriculum) => (
            <Card key={curriculum.id} className="border-slate-200 shadow-card hover:shadow-float transition-all duration-300" data-testid={`curriculum-card-${curriculum.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg font-heading">{curriculum.title}</CardTitle>
                  <Badge className="bg-blue-100 text-blue-700">
                    {curriculum.job_role}
                  </Badge>
                </div>
                {curriculum.description && (
                  <CardDescription className="line-clamp-2">{curriculum.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <PlayCircle className="w-4 h-4 mx-auto text-blue-600 mb-1" />
                    <span className="text-sm font-medium">{curriculum.training_node_ids?.length || 0}</span>
                    <p className="text-xs text-slate-500">Trainings</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <FileQuestion className="w-4 h-4 mx-auto text-emerald-600 mb-1" />
                    <span className="text-sm font-medium">{curriculum.quiz_ids?.length || 0}</span>
                    <p className="text-xs text-slate-500">Quizzes</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <BookOpen className="w-4 h-4 mx-auto text-amber-600 mb-1" />
                    <span className="text-sm font-medium">{curriculum.sop_ids?.length || 0}</span>
                    <p className="text-xs text-slate-500">SOPs</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span>{curriculum.duration_days} days to complete</span>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex-1"
                    onClick={() => handleOpenAssign(curriculum)}
                    data-testid={`assign-curriculum-${curriculum.id}`}
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Assign
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleEdit(curriculum)}
                    data-testid={`edit-curriculum-${curriculum.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(curriculum.id)}
                    data-testid={`delete-curriculum-${curriculum.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Curriculum Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedCurriculum ? 'Edit Curriculum' : 'Create Curriculum'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Clin Ops Onboarding"
                  data-testid="curriculum-title-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of this curriculum"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Job Role</Label>
                <Select 
                  value={form.job_role}
                  onValueChange={(v) => setForm({ ...form, job_role: v })}
                >
                  <SelectTrigger data-testid="job-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_ROLES.map(role => (
                      <SelectItem key={role} value={role}>{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.duration_days}
                  onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) })}
                />
              </div>
            </div>

            {/* Auto-populate button */}
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                onClick={handleAutoPopulate}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Auto-populate Mandatory Items for {form.job_role}
              </Button>
            </div>

            <Tabs defaultValue="trainings" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="trainings">Trainings ({form.training_node_ids.length})</TabsTrigger>
                <TabsTrigger value="sops">SOPs ({form.sop_ids.length})</TabsTrigger>
                <TabsTrigger value="quizzes">Quizzes ({form.quiz_ids.length})</TabsTrigger>
                <TabsTrigger value="activities">Activities ({form.activity_ids.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="trainings" className="mt-4">
                <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                  {trainingNodes.map(node => {
                    const requirement = node.role_requirements?.[form.job_role] || '';
                    return (
                      <div key={node.id} className="flex items-center space-x-2 py-1">
                        <Checkbox
                          id={`node-${node.id}`}
                          checked={form.training_node_ids.includes(node.id)}
                          onCheckedChange={() => toggleItem('training_node_ids', node.id)}
                        />
                        <label htmlFor={`node-${node.id}`} className="text-sm cursor-pointer flex-1">
                          {node.label}
                        </label>
                        {requirement === 'M' && (
                          <Badge className="bg-red-100 text-red-700 text-xs">M</Badge>
                        )}
                        {requirement === 'R' && (
                          <Badge variant="outline" className="text-xs">R</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="sops" className="mt-4">
                <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                  {sops.map(sop => {
                    const requirement = sop.role_requirements?.[form.job_role] || '';
                    return (
                      <div key={sop.id} className="flex items-center space-x-2 py-1">
                        <Checkbox
                          id={`sop-${sop.id}`}
                          checked={form.sop_ids.includes(sop.id)}
                          onCheckedChange={() => toggleItem('sop_ids', sop.id)}
                        />
                        <label htmlFor={`sop-${sop.id}`} className="text-sm cursor-pointer flex-1">
                          {sop.sop_number && <span className="font-mono text-slate-500 mr-1">{sop.sop_number}</span>}
                          {sop.title.replace(/^[A-Z]+-\d+\s*-\s*/, '')}
                        </label>
                        {requirement === 'M' && (
                          <Badge className="bg-red-100 text-red-700 text-xs">M</Badge>
                        )}
                        {requirement === 'R' && (
                          <Badge variant="outline" className="text-xs">R</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="quizzes" className="mt-4">
                <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                  {quizzes.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No quizzes created yet</p>
                  ) : (
                    quizzes.map(quiz => (
                      <div key={quiz.id} className="flex items-center space-x-2 py-1">
                        <Checkbox
                          id={`quiz-${quiz.id}`}
                          checked={form.quiz_ids.includes(quiz.id)}
                          onCheckedChange={() => toggleItem('quiz_ids', quiz.id)}
                        />
                        <label htmlFor={`quiz-${quiz.id}`} className="text-sm cursor-pointer">
                          {quiz.title}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="activities" className="mt-4">
                <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                  {activities.map(activity => (
                    <div key={activity.id} className="flex items-center space-x-2 py-1">
                      <Checkbox
                        id={`activity-${activity.id}`}
                        checked={form.activity_ids.includes(activity.id)}
                        onCheckedChange={() => toggleItem('activity_ids', activity.id)}
                      />
                      <label htmlFor={`activity-${activity.id}`} className="text-sm cursor-pointer flex-1">
                        {activity.title}
                      </label>
                      <Badge variant="outline" className="text-xs capitalize">
                        {activity.activity_type}
                      </Badge>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-curriculum-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Curriculum
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Assign Curriculum
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Assign <strong>{selectedCurriculum?.title}</strong> ({selectedCurriculum?.job_role}) to:
            </p>
            
            {/* Filter employees by matching job_role */}
            {(() => {
              const matchingUsers = users.filter(u => u.job_role === selectedCurriculum?.job_role);
              const otherUsers = users.filter(u => u.job_role !== selectedCurriculum?.job_role);
              
              return (
                <div className="space-y-4">
                  {/* Bulk assign option */}
                  {matchingUsers.length > 0 && (
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-emerald-800">
                            Bulk Assign to All {selectedCurriculum?.job_role}
                          </p>
                          <p className="text-xs text-emerald-600 mt-0.5">
                            {matchingUsers.length} employee(s) with matching job role
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          onClick={handleBulkAssign}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="bulk-assign-btn"
                        >
                          Assign All
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {matchingUsers.length > 0 && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-sm font-medium text-indigo-800 mb-2">
                        Or select individual ({selectedCurriculum?.job_role}):
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {matchingUsers.map(u => (
                          <div 
                            key={u.id} 
                            className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                              selectedUserId === u.id ? 'bg-blue-100' : 'hover:bg-blue-100/50'
                            }`}
                            onClick={() => setSelectedUserId(u.id)}
                          >
                            <span className="text-sm">{u.name} ({u.email})</span>
                            {selectedUserId === u.id && <CheckCircle className="w-4 h-4 text-blue-600" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Or select any employee:</Label>
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                      <SelectTrigger data-testid="assign-user-select">
                        <SelectValue placeholder="Choose an employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({user.email})
                            {user.job_role && <span className="text-slate-400 ml-1">- {user.job_role}</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!selectedUserId} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-assign-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Assign Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Curricula;
